import { useCallback, useEffect, useMemo, useState } from "react";
import { listarCiclos } from "../../../../services/ciclosService";
import { normalizeCiclo } from "../adapters/ciclosAdapter";
import { showToast } from "../../../../utils/showToast";

export default function useCiclosList() {
  const [ciclos, setCiclos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [filtro, setFiltro] = useState({ tipo: "all", valor: null });
  const [filtros, setFiltros] = useState({
    search: "",
    estado: "all",
    activo: "all",
    centro_id: "all",
    anio: "all",
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const fetchCiclos = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listarCiclos();
      const normalized = (Array.isArray(data) ? data : []).map(normalizeCiclo);
      setCiclos(normalized);
      setListError(null);
      setLastUpdated(new Date());
    } catch (err) {
      console.error(err);
      const message = err.message || "No se pudo obtener la lista de ciclos.";
      setListError(message);
      showToast({ type: "danger", message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCiclos();
  }, [fetchCiclos]);

  const resumen = useMemo(() => {
    const total = ciclos.length;
    const activas = ciclos.filter((item) => item.activo).length;
    const inactivas = total - activas;
    const planificados = ciclos.filter(
      (item) => (item.estado || "").toLowerCase() === "planificado"
    ).length;
    const activosEstado = ciclos.filter(
      (item) => (item.estado || "").toLowerCase() === "activo"
    ).length;
    const cerrados = ciclos.filter(
      (item) => (item.estado || "").toLowerCase() === "cerrado"
    ).length;
    const cancelados = ciclos.filter(
      (item) => (item.estado || "").toLowerCase() === "cancelado"
    ).length;

    return {
      total,
      activas,
      inactivas,
      planificados,
      activosEstado,
      cerrados,
      cancelados,
    };
  }, [ciclos]);

  const ciclosFiltrados = useMemo(() => {
    let result = ciclos.filter((item) => {
      if (filtros.activo === "1" && !item.activo) return false;
      if (filtros.activo === "0" && item.activo) return false;
      if (filtros.estado !== "all" && filtros.estado) {
        if ((item.estado || "").toLowerCase() !== String(filtros.estado || "").toLowerCase()) {
          return false;
        }
      }
      if (filtros.centro_id !== "all" && filtros.centro_id) {
        if (String(item.centroId || "") !== String(filtros.centro_id || "")) {
          return false;
        }
      }
      if (filtros.anio !== "all" && filtros.anio) {
        if (String(item.anio || "") !== String(filtros.anio || "")) {
          return false;
        }
      }
      if (filtros.search) {
        const term = filtros.search.toLowerCase();
        const hay =
          (item.codigo || "").toLowerCase().includes(term) ||
          (item.nombre || "").toLowerCase().includes(term) ||
          (item.observaciones || "").toLowerCase().includes(term) ||
          (item.centroLabel || "").toLowerCase().includes(term);
        if (!hay) return false;
      }
      return true;
    });

    if (filtro.tipo === "estado") {
      result = result.filter(
        (item) => (item.estado || "").toLowerCase() === String(filtro.valor || "").toLowerCase()
      );
    }
    if (filtro.tipo === "activo") {
      result = result.filter((item) => Boolean(item.activo) === Boolean(filtro.valor));
    }
    return result;
  }, [ciclos, filtros, filtro]);

  const total = ciclosFiltrados.length;
  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / pageSize)), [total, pageSize]);

  useEffect(() => {
    setPage(1);
  }, [filtros, filtro, total]);

  const ciclosPaginados = useMemo(() => {
    const start = (page - 1) * pageSize;
    return ciclosFiltrados.slice(start, start + pageSize);
  }, [ciclosFiltrados, page, pageSize]);

  const goToPage = useCallback(
    (nextPage) => {
      const safe = Math.min(totalPages, Math.max(1, nextPage));
      setPage(safe);
    },
    [totalPages]
  );

  const aplicarFiltro = useCallback((tipo, valor = null) => {
    setFiltro((prev) => {
      const same = prev.tipo === tipo && prev.valor === valor;
      return same ? { tipo: "all", valor: null } : { tipo, valor };
    });
  }, []);

  const upsertCiclo = useCallback((ciclo) => {
    setCiclos((prev) => {
      const exists = prev.some((item) => String(item.id) === String(ciclo.id));
      return exists
        ? prev.map((item) => (String(item.id) === String(ciclo.id) ? ciclo : item))
        : [ciclo, ...prev];
    });
    setLastUpdated(new Date());
  }, []);

  const removeCiclo = useCallback((id) => {
    setCiclos((prev) => prev.filter((item) => String(item.id) !== String(id)));
    setLastUpdated(new Date());
  }, []);

  const limpiarFiltros = useCallback(
    () =>
      setFiltros({
        search: "",
        estado: "all",
        activo: "all",
        centro_id: "all",
        anio: "all",
      }),
    []
  );

  return {
    ciclos,
    ciclosFiltrados,
    ciclosPaginados,
    resumen,
    filtro,
    filtros,
    setFiltros,
    limpiarFiltros,
    aplicarFiltro,
    loading,
    listError,
    lastUpdated,
    page,
    pageSize,
    setPageSize,
    totalPages,
    total,
    goToPage,
    reload: fetchCiclos,
    setCiclos,
    upsertCiclo,
    removeCiclo,
  };
}
